import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { SocialLoginButtons, Divider } from '@/components/auth'
import { useAuth } from '@/hooks'
import { signupSchema, type SignupFormData } from '@/lib/validations'

export function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    signUp,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    isLoading,
    error,
    clearError,
  } = useAuth()
  const [showPassword, setShowPassword] = useState(false)

  // Support redirect from query params (for invite flow) and email prefill
  const redirectParam = searchParams.get('redirect')
  const emailParam = searchParams.get('email')
  const redirectTo = redirectParam || '/'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: emailParam || '',
    },
  })

  const onSubmit = async (data: SignupFormData) => {
    clearError()
    try {
      await signUp({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        employerCode: data.employerCode,
      })
      navigate(redirectTo, { replace: true })
    } catch {
      // Error is handled by useAuth
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'apple') => {
    clearError()
    try {
      if (provider === 'google') await signInWithGoogle()
      else if (provider === 'facebook') await signInWithFacebook()
      else if (provider === 'apple') await signInWithApple()
      navigate(redirectTo, { replace: true })
    } catch {
      // Error is handled by useAuth
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-serif font-semibold text-gray-900">Join FamSteady</h2>
        <p className="mt-2 text-sm text-gray-600">
          Create an account to start looking out for your family.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-error-50 p-4">
          <p className="text-sm text-error-700">{error}</p>
        </div>
      )}

      <SocialLoginButtons
        onGoogleClick={() => handleSocialLogin('google')}
        onFacebookClick={() => handleSocialLogin('facebook')}
        onAppleClick={() => handleSocialLogin('apple')}
        disabled={isLoading}
      />

      <Divider text="or register with email" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              type="text"
              autoComplete="given-name"
              placeholder="John"
              className="mt-1"
              {...register('firstName')}
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-error-600">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              type="text"
              autoComplete="family-name"
              placeholder="Doe"
              className="mt-1"
              {...register('lastName')}
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-error-600">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="mt-1"
            {...register('email')}
          />
          {errors.email && <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>}
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <div className="relative mt-1">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className="pr-10"
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            className="mt-1"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-error-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="phone">
            Phone number <span className="text-gray-400">(optional)</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            placeholder="+1 (555) 123-4567"
            className="mt-1"
            {...register('phone')}
          />
          {errors.phone && <p className="mt-1 text-sm text-error-600">{errors.phone.message}</p>}
        </div>

        <div>
          <Label htmlFor="employerCode">
            Employer code <span className="text-gray-400">(optional)</span>
          </Label>
          <Input
            id="employerCode"
            type="text"
            placeholder="ACME2025"
            className="mt-1"
            {...register('employerCode')}
          />
          {errors.employerCode && (
            <p className="mt-1 text-sm text-error-600">{errors.employerCode.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            If your employer provides FamSteady, enter the code here
          </p>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Creating account...' : 'Create account'}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link
          to={redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : '/login'}
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign in
        </Link>
      </p>
    </div>
  )
}
